class PhoneCasePersonalizer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abort = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-config]').textContent);
    window.GiftDecoration?.bind(this,this.abort.signal,()=>this.render());
    this.canvas = this.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.form = this.querySelector('form');
    this.image = null;
    this.baseCache = new Map();
    this.baseImage = null;
    this.baseName = null;
    this.baseFailed = false;
    this.baseSequence = 0;
    this.busy = false;
    this.fileSequence = 0;
    this.querySelectorAll('input,select').forEach(input => input.addEventListener('input', () => this.render(), {signal:this.abort.signal}));
    this.querySelector('[data-upload]')?.addEventListener('change', e => this.loadPhoto(e.target), {signal:this.abort.signal});
    this.querySelector('[data-reset]').addEventListener('click', () => {
      ['x','y'].forEach(k => this.querySelector('[data-field="'+k+'"]').value = 0);
      this.querySelector('[data-field="zoom"]').value = 100;
      this.render();
    }, {signal:this.abort.signal});
    this.querySelector('[data-download]').addEventListener('click', () => this.download(), {signal:this.abort.signal});
    this.form.addEventListener('submit', e => this.submit(e), {signal:this.abort.signal});
    this.canvas.addEventListener('pointerdown', e => {
      if (!this.image || this.busy) return;
      const s = this.state();
      this.drag = {px:e.clientX,py:e.clientY,x:s.x,y:s.y};
      this.canvas.setPointerCapture(e.pointerId);
    }, {signal:this.abort.signal});
    this.canvas.addEventListener('pointermove', e => {
      if (!this.drag) return;
      const box = this.canvas.getBoundingClientRect();
      this.querySelector('[data-field="x"]').value = Math.max(-100,Math.min(100,this.drag.x + (e.clientX-this.drag.px)/box.width*200));
      this.querySelector('[data-field="y"]').value = Math.max(-100,Math.min(100,this.drag.y + (e.clientY-this.drag.py)/box.height*200));
      this.render();
    }, {signal:this.abort.signal});
    ['pointerup','pointercancel','lostpointercapture'].forEach(type => this.canvas.addEventListener(type, () => { this.drag=null; }, {signal:this.abort.signal}));
    this.brand = this.querySelector('[data-brand]');
    this.modelSelect = this.querySelector('[data-model]');
    this.brand.addEventListener('change', () => this.populateModels(), {signal:this.abort.signal});
    this.modelSelect.addEventListener('change', () => { this.model = window.PhoneCaseModels.find(m=>m.name===this.modelSelect.value); this.loadBase(); this.render(); }, {signal:this.abort.signal});
    this.populateModels();
  }
  populateModels() {
    const previous = this.modelSelect.value;
    const models = window.PhoneCaseModels.filter(m => m.brand === this.brand.value);
    this.modelSelect.replaceChildren(...models.map(m => new Option(m.name,m.name)));
    if(models.some(m=>m.name===previous)) this.modelSelect.value=previous;
    this.model = models.find(m=>m.name===this.modelSelect.value);
    this.loadBase();
    this.render();
  }
  decoration(){return window.GiftDecoration?.style(this)||'original';}
  disconnectedCallback() {
    ++this.baseSequence;
    this.abort?.abort();
    if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
    this.initialized = false;
  }
  async loadBase() {
    const model = this.model, seq = ++this.baseSequence;
    this.baseImage = null; this.baseName = null; this.baseFailed = false;
    if (this.config.kind !== 'phone') return;
    if (!model?.preview?.url) { this.baseFailed = true; return; }
    let pending = this.baseCache.get(model.name);
    if (!pending) {
      pending = (async () => {
        const image = new Image(); image.crossOrigin = 'anonymous'; image.src = model.preview.url;
        await image.decode(); return image;
      })();
      this.baseCache.set(model.name, pending);
    }
    try {
      const image = await pending;
      if (seq !== this.baseSequence || !this.isConnected) return;
      this.baseImage = image; this.baseName = model.name; this.render();
    } catch {
      this.baseCache.delete(model.name);
      if (seq !== this.baseSequence || !this.isConnected) return;
      this.baseFailed = true; this.render();
    }
  }
  baseReady() { return this.config.kind !== 'phone' || !!this.baseImage && this.baseName === this.model?.name; }
  drawBase() {
    const g = this.model.preview;
    this.ctx.drawImage(this.baseImage, ...g.crop, 0, 0, this.canvas.width, this.canvas.height);
  }
  caseGeometry() {
    const w=this.canvas.width,h=this.canvas.height,inset=w*.047;
    return {w,h,inset,radius:Math.max(34,this.model.corner*w/this.model.width+22)};
  }
  casePalette() {
    const color=this.querySelector('[data-field="caseColor"]')?.value || 'Schwarz';
    if(color==='Weiß') return {stops:['#ffffff','#e9e7e2','#fdfcf9','#c9c7c2'],edge:'#bab7af',lip:'#d2cfc8',shine:'#ffffff',camera:'#e8e5df',cameraShine:'#ffffff'};
    if(color==='Durchsichtig') return {stops:['#f5fbffb8','#b4c3cc82','#e1edf4a6','#a5b6c180'],edge:'#f8fcffdc',lip:'#b7c7d3b8',shine:'#ffffffeb',camera:'#b8c9d2c7',cameraShine:'#ffffffeb'};
    return {stops:['#56534f','#242321','#35332f','#151514'],edge:'#77716a',lip:'#151513',shine:'#67615a',camera:'#262522',cameraShine:'#69635b'};
  }
  drawCaseBody() {
    const ctx=this.ctx,{w,h,inset,radius}=this.caseGeometry();
    const palette=this.casePalette();
    if(this.state().caseColor==='Durchsichtig') {ctx.save();ctx.beginPath();ctx.roundRect(4,4,w-8,h-8,radius);ctx.clip();this.drawBase();ctx.restore();}
    const shell=ctx.createLinearGradient(0,0,w,h);
    [0,.12,.5,1].forEach((position,i)=>shell.addColorStop(position,palette.stops[i]));
    ctx.fillStyle=shell;ctx.beginPath();ctx.roundRect(4,4,w-8,h-8,radius);ctx.fill();
    ctx.lineWidth=3;ctx.strokeStyle=palette.edge;ctx.beginPath();ctx.roundRect(10,10,w-20,h-20,radius-5);ctx.stroke();
    ctx.fillStyle='#fcfaf7';ctx.beginPath();ctx.roundRect(inset,inset,w-inset*2,h-inset*2,radius-inset);ctx.fill();
  }
  drawCaseLip() {
    const ctx=this.ctx,{w,h,inset,radius}=this.caseGeometry();
    const palette=this.casePalette();
    ctx.save();ctx.lineWidth=9;ctx.strokeStyle=palette.lip;ctx.beginPath();ctx.roundRect(inset,inset,w-inset*2,h-inset*2,radius-inset);ctx.stroke();
    ctx.lineWidth=3;ctx.strokeStyle=palette.shine;ctx.beginPath();ctx.roundRect(inset-8,inset-8,w-inset*2+16,h-inset*2+16,radius-inset+8);ctx.stroke();ctx.restore();
  }
  protectCamera() {
    const ctx = this.ctx, g = this.model.preview, [x,y,w,h] = g.camera;
    ctx.save();ctx.beginPath();ctx.roundRect(x*this.canvas.width,y*this.canvas.height,w*this.canvas.width,h*this.canvas.height,g.radius*this.canvas.width);ctx.clip();
    this.drawBase();ctx.restore();
    ctx.save();ctx.beginPath();ctx.roundRect(x*this.canvas.width,y*this.canvas.height,w*this.canvas.width,h*this.canvas.height,g.radius*this.canvas.width);
    const palette=this.casePalette();ctx.strokeStyle=palette.camera;ctx.lineWidth=20;ctx.stroke();ctx.strokeStyle=palette.cameraShine;ctx.lineWidth=3;ctx.stroke();ctx.restore();
  }
  state() {
    const val = name => this.querySelector('[data-field="'+name+'"]').value;
    return {photoMode:this.dataset.readyTheme==='flag'?'flag':'cover',decoration:this.decoration(),kind:this.config.kind,brand:this.brand?.value,model:this.model?.name,caseColor:val('caseColor')||'Schwarz',deviceWidthMm:this.model?.width,deviceHeightMm:this.model?.height,geometryStatus:'preview-awaiting-supplier',text:val('text'),subtitle:val('subtitle'),font:val('font'),size:+val('size'),color:val('color'),background:val('background'),area:val('area'),shape:val('shape'),zoom:+val('zoom'),x:+val('x'),y:+val('y')};
  }
  async loadPhoto(input) {
    const seq = ++this.fileSequence;
    this.image = null;
    if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
    this.photoUrl = null;
    const file = input.files[0];
    this.render();
    if (!file) return;
    if (!['image/jpeg','image/png','image/webp'].includes(file.type) || file.size > 15*1024*1024) {
      input.value = '';
      this.status(this.config.messages.invalid);
      return;
    }
    const url = URL.createObjectURL(file);
    this.photoUrl = url;
    const image = new Image();
    try {
      image.src = url;
      await image.decode();
      if (seq !== this.fileSequence || !this.isConnected) return;
      this.image = image;
      ['x','y'].forEach(k => this.querySelector('[data-field="'+k+'"]').value=0);
      this.querySelector('[data-field="zoom"]').value=100;
      this.render();
      if (Math.min(image.naturalWidth,image.naturalHeight)<600) this.status(this.config.messages.resolution);
    } catch {
      if (seq !== this.fileSequence) return;
      input.value='';this.image=null;this.render();this.status(this.config.messages.invalid);
    }
  }
  status(text) { this.querySelector('[data-status]').textContent = text; }
  render() {
    if(!this.model) return;
    const s = this.state(), c=this.canvas, ctx=this.ctx;
    const mat = s.kind==='text' || s.kind==='photo-mat';
    c.width = mat?1600:900;
    c.height = mat?1000:s.kind==='phone'?Math.round(900*this.model.height/this.model.width):s.shape==='long'?1350:900;
    const w=c.width,h=c.height;
    ctx.clearRect(0,0,w,h);
    const baseReady = this.baseReady();
    if(s.kind==='phone' && baseReady) this.drawCaseBody();
    ctx.save();
    ctx.beginPath();
    if (mat) ctx.rect(0,0,w,h);
    else if (s.kind==='phone') {const g=this.caseGeometry();ctx.roundRect(g.inset,g.inset,w-g.inset*2,h-g.inset*2,g.radius-g.inset);}
    else if (s.shape==='long') ctx.roundRect(22,22,w-44,h-44,160);
    else ctx.arc(w/2,h/2,w/2-22,0,Math.PI*2);
    ctx.clip();
    if(s.kind!=='phone'){ctx.fillStyle = mat?s.background:'#ffffff';ctx.fillRect(0,0,w,h);}
    if (s.kind!=='text' && baseReady) {
      const area = mat?{x:w*.12,y:h*.26,w:w*.76,h:h*.54}:s.area==='center'?{x:w*.15,y:h*.35,w:w*.7,h:h*.48}:{x:0,y:0,w,h};
      ctx.save();ctx.beginPath();ctx.rect(area.x,area.y,area.w,area.h);ctx.clip();
      if (this.image) {
        const flagMode=this.dataset.readyTheme==='flag'&&window.GiftReadyDesigns?.drawFlag;
        if(flagMode){const top=Math.max(area.y,h*.29),bottom=Math.min(area.y+area.h,h*.83),safe={x:area.x+area.w*.10,y:top,w:area.w*.80,h:Math.max(1,bottom-top)};window.GiftReadyDesigns.drawFlag(this,ctx,this.image,safe);}
        else {
        const cover = Math.max(area.w/this.image.naturalWidth,area.h/this.image.naturalHeight)*s.zoom/100;
        const dw=this.image.naturalWidth*cover,dh=this.image.naturalHeight*cover;
        const dx=(dw-area.w)/2*s.x/100,dy=(dh-area.h)/2*s.y/100;
        if(window.GiftDecoration){const frame=s.kind==='phone'?{x:area.x+w*.055,y:Math.max(area.y,h*.24),w:area.w-w*.11,h:Math.min(area.h,h*.54)}:area;window.GiftDecoration.photo(ctx,this.image,{x:area.x+(area.w-dw)/2+dx,y:area.y+(area.h-dh)/2+dy,w:dw,h:dh},this.decoration(),{frame,transform:{scale:s.zoom/100,x:s.x/100,y:s.y/100}});}else ctx.drawImage(this.image,area.x+(area.w-dw)/2+dx,area.y+(area.h-dh)/2+dy,dw,dh);
        }
      } else {
        ctx.fillStyle=s.kind==='phone'?'#fcfaf7':'#f0e8dc';ctx.fillRect(area.x,area.y,area.w,area.h);
        ctx.fillStyle='#796650';ctx.font='600 '+Math.min(area.w*.09,70)+'px Arial';ctx.textAlign='center';
        ctx.fillText(this.config.messages.placeholder,area.x+area.w/2,area.y+area.h/2);
      }
      ctx.restore();
    }
    ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillStyle=s.color;
    const font={sans:'Arial, sans-serif',serif:'Georgia, serif',script:'cursive'}[s.font];
    const write = (text,y,size,maxWidth) => {
      ctx.font='600 '+size+'px '+font;
      while(ctx.measureText(text).width>maxWidth && size>20) { size--;ctx.font='600 '+size+'px '+font; }
      ctx.fillText(text,w/2,y);
    };
    if(mat) {
      write(s.text,s.kind==='text'?h*.4:h*.13,s.size*2.4,w*.88);
      write(s.subtitle,s.kind==='text'?h*.64:h*.9,s.size*1.3,w*.88);
      if(s.kind==='text') {ctx.font='60px Arial';ctx.fillText('♡',w/2,h*.8);}
    } else {
      write(s.text,h*.86,s.size*1.4,w*.78);
      write(s.subtitle,h*.93,s.size*.9,w*.74);
    }
    // This is a design preview; the manufacturer must confirm the physical print mask.
    if(s.kind==='phone' && baseReady) this.protectCamera();
    if(s.kind==='keychain') {
      ctx.fillStyle='#b8b8b8';ctx.beginPath();ctx.arc(w/2,s.shape==='long'?80:60,20,0,Math.PI*2);ctx.fill();
    }
    ctx.restore();
    if(s.kind==='phone' && baseReady) this.drawCaseLip();
    this.querySelector('[data-model-name]').textContent = this.model.name;
    this.querySelector('[data-dimensions]').textContent = this.config.dimensionsLabel+' '+this.model.width.toLocaleString('de-DE')+' × '+this.model.height.toLocaleString('de-DE')+' mm';
    this.querySelector('[data-transform]').value=JSON.stringify({...s,width:w,height:h,version:'phone-case-colors-v4-20261011',positionUnit:'percent-overflow'});
    const variant = this.config.variants.find(v=>String(v.id)===this.querySelector('[name="id"]').value);
    const sellable = this.config.productionReady && variant && variant.available && variant.price>0;
    const hasPhoto = s.kind==='text'||!!this.image;
    this.querySelector('[data-buy]').disabled=this.busy||!sellable||!hasPhoto||!baseReady;
    this.querySelector('[data-download]').disabled=this.busy||!hasPhoto||!baseReady;
    this.querySelector('fieldset').disabled=this.busy||!this.image;
    this.status(this.baseFailed?this.config.messages.failed:!baseReady?this.config.messages.loading:!sellable?this.config.messages.soon:!hasPhoto?this.config.messages.upload:this.config.messages.ready);
    this.querySelectorAll('[data-value]').forEach(out=>out.textContent=this.querySelector('[data-field="'+out.dataset.value+'"]').value+'%');
  }
  blob() { if (!this.baseReady()) return Promise.reject(new Error('base-not-ready')); return new Promise((resolve,reject)=>this.canvas.toBlob(blob=>blob?resolve(blob):reject(new Error('export')),'image/png')); }
  async download() {
    try {
      const url=URL.createObjectURL(await this.blob());
      const a=document.createElement('a');a.href=url;a.download='mein-'+this.config.kind+'-design.png';a.click();
      setTimeout(()=>URL.revokeObjectURL(url),1000);
    } catch {this.status(this.config.messages.failed);}
  }
  async submit(e) {
    e.preventDefault();
    if(this.busy || this.querySelector('[data-buy]').disabled) return;
    this.busy=true;this.render();this.status(this.config.messages.saving);
    try {
      const s=this.state(), transfer=new DataTransfer();
      transfer.items.add(new File([await this.blob()],'personalisiertes-design.png',{type:'image/png'}));
      this.querySelector('[data-design-file]').files=transfer.files;
      this.querySelector('[data-transform]').value=JSON.stringify({...s,width:this.canvas.width,height:this.canvas.height,version:'phone-case-colors-v4-20261011',positionUnit:'percent-overflow'});
      HTMLFormElement.prototype.submit.call(this.form);
    } catch {this.busy=false;this.render();this.status(this.config.messages.failed);}
  }
}
if (!customElements.get('phone-case-personalizer')) customElements.define('phone-case-personalizer',PhoneCasePersonalizer);


